import { useEffect, useMemo, useRef } from "react";
import { MapContainer, TileLayer, Marker, useMap, Polyline } from "react-leaflet";
import L from "leaflet";
import "leaflet.markercluster";
import "leaflet.markercluster/dist/MarkerCluster.css";

const HOUSE_SVG = `<path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z"/><polyline points="9 22 9 12 15 12 15 22"/>`;
const BRIEFCASE_SVG = `<rect x="2" y="7" width="20" height="14" rx="2"/><path d="M16 21V5a2 2 0 0 0-2-2h-4a2 2 0 0 0-2 2v16"/>`;

function pinIcon(highlighted, topRated, business = false) {
  const cls = topRated ? "ys-pin-top" : (highlighted ? "ys-pin-highlight" : (business ? "ys-pin-business" : "ys-pin-standard"));
  return L.divIcon({
    className: "",
    html: `<div class="ys-pin ${cls}${business ? " ys-pin-biz" : ""}" data-kind="${business ? "business" : "sale"}">
      <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#fff" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round">${business ? BRIEFCASE_SVG : HOUSE_SVG}</svg>
    </div>`,
    iconSize: [34, 34],
    iconAnchor: [17, 34],
  });
}

const gpsIcon = L.divIcon({ className: "", html: `<div class="ys-gps-dot"></div>`, iconSize: [18, 18], iconAnchor: [9, 9] });

function ClusterLayer({ markers, topSet, onSelect }) {
  const map = useMap();
  const onSelectRef = useRef(onSelect);
  onSelectRef.current = onSelect;
  useEffect(() => {
    const group = L.markerClusterGroup({
      showCoverageOnHover: false,
      maxClusterRadius: 55,
      spiderfyOnMaxZoom: true,
      chunkedLoading: true,
      iconCreateFunction: (cluster) => {
        const count = cluster.getChildCount();
        const size = count < 10 ? 38 : count < 50 ? 46 : 54;
        return L.divIcon({
          html: `<div class="ys-cluster"><span>${count}</span></div>`,
          className: "",
          iconSize: [size, size],
          iconAnchor: [size / 2, size / 2],
        });
      },
    });
    markers.forEach((l) => {
      const biz = l.kind === "business";
      const m = L.marker([l.lat, l.lng], { icon: pinIcon(l.highlighted, topSet.has(l.id), biz) });
      if (biz) {
        const esc = (s) => String(s || "").replace(/[&<>"]/g, (c) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;" }[c]));
        m.bindTooltip(`<span class="ys-biz-tip-label">Business</span> ${esc(l.title)}${l.business_type ? ` · ${esc(l.business_type)}` : ""}`, { direction: "top", offset: [0, -30], className: "ys-cluster-tip", opacity: 1 });
      }
      m.on("click", () => onSelectRef.current && onSelectRef.current(l));
      group.addLayer(m);
    });
    const label = (layer) => { const n = layer.getChildCount(); return `${n} ${n === 1 ? "sale" : "sales"} here`; };
    group.on("clustermouseover", (e) => {
      e.layer.bindTooltip(label(e.layer), { direction: "top", offset: [0, -6], className: "ys-cluster-tip", opacity: 1 }).openTooltip();
    });
    group.on("clustermouseout", (e) => e.layer.closeTooltip());
    group.on("clusterclick", (e) => {
      e.layer.bindTooltip(label(e.layer), { direction: "top", offset: [0, -6], className: "ys-cluster-tip", opacity: 1 }).openTooltip();
      setTimeout(() => e.layer.closeTooltip(), 1300);
    });
    map.addLayer(group);
    return () => { map.removeLayer(group); };
  }, [map, markers, topSet]);
  return null;
}

function FitBounds({ markers, trigger }) {
  const map = useMap();
  useEffect(() => {
    if (!trigger || !markers.length) return;
    const pts = markers.map((l) => [l.lat, l.lng]);
    if (pts.length === 1) map.setView(pts[0], 14, { animate: true });
    else map.fitBounds(pts, { padding: [60, 60], maxZoom: 15, animate: true });
  }, [trigger, markers, map]);
  return null;
}

function Recenter({ center }) {
  const map = useMap();
  const lastRef = useRef(null);
  useEffect(() => {
    if (!center) return;
    const key = `${center[0].toFixed(5)},${center[1].toFixed(5)}`;
    if (lastRef.current === key) return;
    lastRef.current = key;
    map.setView(center, map.getZoom() < 11 ? 12 : map.getZoom(), { animate: true });
  }, [center, map]);
  return null;
}

function DraggablePicker({ position, onChange }) {
  const map = useMap();
  useEffect(() => {
    const handler = (e) => onChange([e.latlng.lat, e.latlng.lng]);
    map.on("click", handler);
    return () => map.off("click", handler);
  }, [map, onChange]);
  if (!position) return null;
  return (
    <Marker position={position} draggable={true} icon={pinIcon(false)}
      eventHandlers={{ dragend: (e) => { const p = e.target.getLatLng(); onChange([p.lat, p.lng]); } }} />
  );
}

export function MapView({
  listings = [], center = [39.5, -98.35], zoom = 4, userLocation = null, topRatedIds = [],
  onSelect, picker = false, pickerPosition = null, onPickerChange, routeLine = null, routeSolid = false, className = "", preview = false, fitTrigger = 0,
}) {
  const markers = useMemo(() => listings.filter((l) => l.lat && l.lng), [listings]);
  const topSet = useMemo(() => new Set(topRatedIds), [topRatedIds]);
  const interactive = !preview;
  return (
    <MapContainer center={center} zoom={zoom} className={className} style={{ height: "100%", width: "100%" }} data-testid="map-container"
      dragging={interactive} scrollWheelZoom={interactive} doubleClickZoom={interactive} touchZoom={interactive}
      boxZoom={interactive} keyboard={interactive} zoomControl={interactive}>
      <TileLayer attribution='&copy; OpenStreetMap' url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" />
      <Recenter center={userLocation || (markers[0] ? [markers[0].lat, markers[0].lng] : null)} />
      <FitBounds markers={markers} trigger={fitTrigger} />
      <ClusterLayer markers={markers} topSet={topSet} onSelect={onSelect} />
      {userLocation && <Marker position={userLocation} icon={gpsIcon} />}
      {routeLine && routeLine.length > 1 && <Polyline positions={routeLine} pathOptions={{ color: "#0284c7", weight: routeSolid ? 5 : 4, dashArray: routeSolid ? null : "8 8" }} />}
      {picker && <DraggablePicker position={pickerPosition} onChange={onPickerChange} />}
    </MapContainer>
  );
}
